import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      initializeAnalytics(savedPreferences.analytics);
    }
  }, []);

  const initializeAnalytics = (enabled: boolean) => {
    if (enabled && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  };

  const handleAcceptAll = () => {
    const allConsent: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allConsent);
  };

  const handleRejectAll = () => {
    const minimalConsent: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(minimalConsent);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (consentPreferences: ConsentPreferences) => {
    localStorage.setItem('cookie_consent', JSON.stringify(consentPreferences));
    initializeAnalytics(consentPreferences.analytics);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const togglePreference = (key: keyof ConsentPreferences) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-7xl mx-auto px-4 pb-6">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Main Banner */}
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                <i className="ri-cookie-line text-2xl text-teal-600"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Çerez Kullanımı ve Gizlilik
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Web sitemizde deneyiminizi geliştirmek, site trafiğini analiz etmek ve kişiselleştirilmiş içerik sunmak için çerezler kullanıyoruz. 
                  Çerezler, tarayıcınızda saklanan küçük metin dosyalarıdır ve sitemizin düzgün çalışması için gereklidir. 
                  Analitik çerezler, Google Analytics aracılığıyla site kullanımını anlamamıza yardımcı olur. 
                  Kişisel verilerinizin işlenmesi hakkında detaylı bilgi için{' '}
                  <Link to="/kvkk" className="text-teal-600 hover:text-teal-700 font-medium underline">
                    KVKK Aydınlatma Metni
                  </Link>
                  'ni inceleyebilirsiniz.
                </p>
              </div>
            </div>

            {/* Preferences Panel */}
            {showPreferences && (
              <div className="mb-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="text-base font-semibold text-gray-900 mb-4">
                  Çerez Tercihlerinizi Yönetin
                </h4>
                <div className="space-y-4">
                  {/* Necessary Cookies */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="text-sm font-semibold text-gray-900">
                          Zorunlu Çerezler
                        </h5>
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-full whitespace-nowrap">
                          Her Zaman Aktif
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Bu çerezler web sitesinin temel işlevleri için gereklidir ve devre dışı bırakılamaz.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-6 bg-gray-300 rounded-full cursor-not-allowed opacity-50"></div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                        Analitik Çerezler
                      </h5>
                      <p className="text-xs text-gray-600">
                        Google Analytics ile site kullanımını analiz eder, ziyaretçi davranışlarını anlar ve hizmetlerimizi geliştirir.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className="flex-shrink-0 relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer"
                      style={{
                        backgroundColor: preferences.analytics ? '#14B8A6' : '#D1D5DB',
                      }}
                      type="button"
                    >
                      <span
                        className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200"
                        style={{
                          transform: preferences.analytics ? 'translateX(24px)' : 'translateX(0)',
                        }}
                      ></span>
                    </button>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                        Pazarlama Çerezleri
                      </h5>
                      <p className="text-xs text-gray-600">
                        Kişiselleştirilmiş reklamlar göstermek ve pazarlama kampanyalarının etkinliğini ölçmek için kullanılır.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className="flex-shrink-0 relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer"
                      style={{
                        backgroundColor: preferences.marketing ? '#14B8A6' : '#D1D5DB',
                      }}
                      type="button"
                    >
                      <span
                        className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200"
                        style={{
                          transform: preferences.marketing ? 'translateX(24px)' : 'translateX(0)',
                        }}
                      ></span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                type="button"
              >
                Tümünü Kabul Et
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                type="button"
              >
                Tümünü Reddet
              </button>
              {showPreferences ? (
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  type="button"
                >
                  Tercihleri Kaydet
                </button>
              ) : (
                <button
                  onClick={() => setShowPreferences(true)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-900 font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  type="button"
                >
                  Tercihleri Yönet
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;